/**
 * getUserImage.ts
 *
 * Fetches user profile image by userId and dimension from the core user API.
 * Returns a data URL string. On failure or missing image, returns a default
 * avatar so callers always get a displayable value. Results are cached by
 * userId+dimension to avoid repeated requests across the app.
 */

// ============= Imports =============

import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============

const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/User/user/image';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {};

/**
 * Default avatar image returned when the API fails or the user has no image.
 * Use as img src anywhere; exported so UI can use the same fallback for
 * workspace-data lookups (e.g. AskHub.utils) without duplicating the asset.
 */
export const DEFAULT_AVATAR_FALLBACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ccircle fill='%23999' cx='16' cy='16' r='16'/%3E%3C/svg%3E";

// ============= Types =============

export interface GetUserImageProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  userId: string;
  dimension: string;
  /** When true, skips cache and fetches from the API (e.g. for refresh). */
  skipCache?: boolean;
}

// ============= Cache =============

/** In-memory cache: key = `${userId}-${dimension}` -> data URL. Reduces repeated API calls across useUserDetails, transformWorkSpaceItems, etc. */
const imageCache = new Map<string, string>();

function cacheKey(userId: string, dimension: string): string {
  return `${userId}-${dimension}`;
}

// ============= Utility Functions =============

/**
 * Creates headers for API request.
 *
 * @param token - Authentication token.
 * @returns Headers object.
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============

/**
 * Fetches user image by user ID and dimension.
 * Returns a data URL string. On any failure (network, 404, etc.) returns
 * DEFAULT_AVATAR_FALLBACK so callers always get a displayable image.
 * Results are cached by userId+dimension; pass skipCache: true to force refresh.
 *
 * @param getAccessToken - Function to acquire access token.
 * @param userId - User identifier.
 * @param dimension - Image dimension (e.g. "48", "96").
 * @param skipCache - If true, bypass cache and refetch.
 * @returns Promise with data URL string (image or fallback).
 */
export const getUserImage = async ({
  getAccessToken,
  userId,
  dimension,
  skipCache = false,
}: GetUserImageProps): Promise<string> => {
  const key = cacheKey(userId, dimension);
  if (!skipCache) {
    const cached = imageCache.get(key);
    if (cached !== undefined) {
      return cached;
    }
  }

  try {
    if (import.meta.env.DEV) {
      console.log('🔄 Fetching user image...');
    }

    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      if (import.meta.env.DEV) {
        console.info('User image unavailable (no token); using fallback avatar.');
      }
      return DEFAULT_AVATAR_FALLBACK;
    }
    tokenManager.logTokenInfo(token, 'API');

    const url = `${BASE_URL}${API_ENDPOINT}/${userId}/${dimension}`;
    const convertURI = encodeURI(url);
    if (import.meta.env.DEV) {
      console.log('🌐 Making request to:', convertURI);
    }

    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token),
    });

    if (!response.ok) {
      if (import.meta.env.DEV) {
        const body = await response.text().catch(() => '');
        const isImageNotFound =
          body.includes('ImageNotFoundException') || body.includes('ODataError');
        console.info(
          `User image not found (${response.status}${isImageNotFound ? ', ImageNotFoundException' : ''}); using fallback avatar.`
        );
      }
      imageCache.set(key, DEFAULT_AVATAR_FALLBACK);
      return DEFAULT_AVATAR_FALLBACK;
    }

    const contentType = response.headers.get('content-type') ?? '';
    const isLikelyErrorBody =
      contentType.includes('application/json') ||
      (contentType.includes('text/') && !contentType.includes('image'));
    if (isLikelyErrorBody) {
      const body = await response.clone().text();
      if (
        body.includes('ImageNotFoundException') ||
        body.includes('Microsoft.Fast.Profile.Core.Exception')
      ) {
        if (import.meta.env.DEV) {
          console.info('User image not found (API error body); using fallback avatar.');
        }
        imageCache.set(key, DEFAULT_AVATAR_FALLBACK);
        return DEFAULT_AVATAR_FALLBACK;
      }
    }

    const blob = await response.blob();
    const imageUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    imageCache.set(key, imageUrl);
    if (import.meta.env.DEV) {
      console.log('✅ User image fetched successfully');
    }
    return imageUrl;
  } catch {
    if (import.meta.env.DEV) {
      console.info('User image unavailable; using fallback avatar.');
    }
    return DEFAULT_AVATAR_FALLBACK;
  }
};
